import { useMemo, useState } from "react";
import { Eye, Loader2 } from "lucide-react";

import { apiClient } from "@/lib/apiClient";
import { useLoanerDirectory } from "../api/hooks";

/** Admin "se som låntaker": pick any synced loaner from a dropdown and open a fresh tab
 *  logged in as that company (a 2h preview token — see POST /admin/impersonate/:id). The
 *  preview never touches this admin's own session (separate tab, own localStorage write
 *  only there) — see PreviewAsLoanerPage. */
export function ImpersonateLoanerCard() {
  const { data: loaners, isLoading } = useLoanerDirectory();
  const [selected, setSelected] = useState<string>("");
  const [opening, setOpening] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const sorted = useMemo(
    () => [...(loaners ?? [])].sort((a, b) => (a.name ?? "").localeCompare(b.name ?? "", "nb")),
    [loaners]
  );

  const openPreview = async () => {
    if (!selected) return;
    setOpening(true);
    setError(null);
    try {
      const r = await apiClient.post<{ ok: true; token: string; companyName: string }>(
        `/admin/impersonate/${selected}`
      );
      const url = new URL("/preview", window.location.origin);
      url.searchParams.set("token", r.token);
      url.searchParams.set("loanerId", selected);
      url.searchParams.set("name", r.companyName);
      window.open(url.toString(), "_blank", "noopener");
    } catch {
      setError("Kunne ikke åpne forhåndsvisning.");
    } finally {
      setOpening(false);
    }
  };

  return (
    <div className="mb-6 flex flex-wrap items-center gap-3 rounded-md border border-white/10 bg-white/[0.04] p-4">
      <Eye className="size-4 shrink-0 text-teal-300" />
      <span className="text-sm font-medium text-white">Se som låntaker</span>
      <select
        value={selected}
        onChange={(e) => setSelected(e.target.value)}
        disabled={isLoading}
        className="min-w-[260px] flex-1 rounded-md border border-white/10 bg-white/5 px-3 py-2 text-sm text-white outline-none focus:border-teal-400/60 disabled:opacity-50"
      >
        <option value="" className="bg-[#0a2a2e]">
          {isLoading ? "Laster …" : "Velg selskap …"}
        </option>
        {sorted.map((l) => (
          <option key={l.id} value={l.id} className="bg-[#0a2a2e]">
            {l.name ?? `Låntaker ${l.id}`} {l.orgNumber ? `(${l.orgNumber})` : ""}
          </option>
        ))}
      </select>
      <button
        type="button"
        onClick={openPreview}
        disabled={!selected || opening}
        className="inline-flex items-center gap-2 rounded-md bg-teal-400/15 px-3 py-2 text-sm font-medium text-teal-300 transition-colors hover:bg-teal-400/25 disabled:opacity-40"
      >
        {opening ? <Loader2 className="size-4 animate-spin" /> : <Eye className="size-4" />}
        Åpne forhåndsvisning
      </button>
      {error && <span className="w-full text-xs text-red-300">{error}</span>}
      <span className="w-full text-[11px] text-white/35">
        Åpnes i ny fane, gyldig i 2 timer — påvirker ikke din egen admin-økt.
      </span>
    </div>
  );
}
